/**
 * Advanced Field Manager Component
 * Allows adding new modules and fields dynamically with sensitivity configuration
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Edit, Save, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  DEPARTMENTS,
  ACCESS_LEVELS,
  SENSITIVITY
} from "../config/permissions";
import { MODULE_FIELDS, getAvailableFields } from "../config/moduleFields";

export default function FieldManager({ fieldConfig, onSave }) {
  const [localConfig, setLocalConfig] = useState(fieldConfig);
  const [addModuleDialog, setAddModuleDialog] = useState(false);
  const [addFieldDialog, setAddFieldDialog] = useState(false);
  const [editFieldDialog, setEditFieldDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  const [newModule, setNewModule] = useState({
    name: '',
    description: ''
  });

  const [newField, setNewField] = useState({
    module: '',
    name: '',
    sensitivity: SENSITIVITY.INTERNAL,
    description: '',
    allowedRoles: []
  });

  const [currentRole, setCurrentRole] = useState({
    department: '',
    levels: []
  });

  const modules = Object.keys(localConfig);

  const handleAddModule = () => {
    if (!newModule.name) {
      toast.error("Module name is required");
      return;
    }

    if (localConfig[newModule.name]) {
      toast.error("Module already exists");
      return;
    }

    setLocalConfig({
      ...localConfig,
      [newModule.name]: {}
    });

    setNewModule({ name: '', description: '' });
    setAddModuleDialog(false);
    toast.success("Module added. Add fields to this module.");
  };

  const handleAddField = () => {
    if (!newField.module) {
      toast.error("Please select a module");
      return;
    }

    if (!newField.name) {
      toast.error("Field name is required");
      return;
    }

    // Check if field already exists in module
    if (localConfig[newField.module]?.[newField.name]) {
      toast.error("Field already exists in this module");
      return;
    }

    const fieldData = {
      sensitivity: newField.sensitivity,
      description: newField.description
    };

    // Add allowed roles if any
    if (newField.allowedRoles.length > 0) {
      fieldData.allowedRoles = newField.allowedRoles;
    }

    // Update config
    const updatedConfig = {
      ...localConfig,
      [newField.module]: {
        ...localConfig[newField.module],
        [newField.name]: fieldData
      }
    };

    setLocalConfig(updatedConfig);
    setNewField({
      module: '',
      name: '',
      sensitivity: SENSITIVITY.INTERNAL,
      description: '',
      allowedRoles: []
    });
    setAddFieldDialog(false);
    toast.success("Field added successfully");
  };

  const handleEditField = () => {
    if (!selectedModule || !selectedField) return;

    const fieldData = {
      sensitivity: newField.sensitivity,
      description: newField.description
    };

    if (newField.allowedRoles.length > 0) {
      fieldData.allowedRoles = newField.allowedRoles;
    }

    const updatedConfig = {
      ...localConfig,
      [selectedModule]: {
        ...localConfig[selectedModule],
        [selectedField]: fieldData
      }
    };

    setLocalConfig(updatedConfig);
    setEditFieldDialog(false);
    setSelectedModule(null);
    setSelectedField(null);
    toast.success("Field updated successfully");
  };

  const handleDeleteField = (module, field) => {
    const updatedModule = { ...localConfig[module] };
    delete updatedModule[field];

    setLocalConfig({
      ...localConfig,
      [module]: updatedModule
    });

    toast.success("Field deleted");
  };

  const handleDeleteModule = (module) => {
    const updated = { ...localConfig };
    delete updated[module];
    setLocalConfig(updated);
    toast.success("Module deleted");
  };

  const handleAddRole = () => {
    if (!currentRole.department) {
      toast.error("Please select a department");
      return;
    }

    const newRole = {
      department: currentRole.department,
      level: currentRole.levels.length > 0 ? currentRole.levels : '*'
    };

    setNewField({
      ...newField,
      allowedRoles: [...newField.allowedRoles, newRole]
    });

    setCurrentRole({ department: '', levels: [] });
  };

  const handleRemoveRole = (index) => {
    const updated = [...newField.allowedRoles];
    updated.splice(index, 1);
    setNewField({ ...newField, allowedRoles: updated });
  };

  const openEditDialog = (module, field) => {
    const fieldData = localConfig[module][field];
    setSelectedModule(module);
    setSelectedField(field);

    setNewField({
      module: module,
      name: field,
      sensitivity: fieldData.sensitivity || SENSITIVITY.INTERNAL,
      description: fieldData.description || '',
      allowedRoles: fieldData.allowedRoles || []
    });

    setEditFieldDialog(true);
  };

  const getSensitivityColor = (level) => {
    switch (level) {
      case SENSITIVITY.PUBLIC: return 'bg-green-100 text-green-800';
      case SENSITIVITY.INTERNAL: return 'bg-blue-100 text-blue-800';
      case SENSITIVITY.CONFIDENTIAL: return 'bg-yellow-100 text-yellow-800';
      case SENSITIVITY.RESTRICTED: return 'bg-red-100 text-red-800';
      default: return 'bg-md3-surface-container text-md3-on-surface';
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button onClick={() => setAddModuleDialog(true)} variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          Add Module
        </Button>
        <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setAddFieldDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Field
        </Button>
        <Button onClick={() => onSave(localConfig)} variant="default">
          <Save className="mr-2 h-4 w-4" />
          Save All Changes
        </Button>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
        <CardContent className="pt-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-md3-body-small text-blue-800">
              <p className="font-semibold mb-1">How to manage fields:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Add Module:</strong> Create a new module (e.g., "invoices", "contracts")</li>
                <li><strong>Add Field:</strong> Add fields to existing or new modules with sensitivity levels</li>
                <li><strong>Sensitivity Levels:</strong> PUBLIC (all) → INTERNAL (logged-in) → CONFIDENTIAL (specific roles) → RESTRICTED (admin only)</li>
                <li><strong>Allowed Roles:</strong> For CONFIDENTIAL/RESTRICTED fields, specify which roles can access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modules and Fields Display */}
      <Tabs defaultValue={modules[0] || ''} className="w-full">
        <TabsList className="w-full flex-wrap h-auto">
          {modules.map(module => (
            <TabsTrigger key={module} value={module}>
              {module}
              <Badge variant="outline" className="ml-2">
                {Object.keys(localConfig[module] || {}).length}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {modules.map(module => (
          <TabsContent key={module} value={module} className="space-y-6">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="capitalize text-md3-title-medium text-md3-on-surface">{module} Fields</CardTitle>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (window.confirm(`Delete entire "${module}" module and all its fields?`)) {
                        handleDeleteModule(module);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Module
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(localConfig[module] || {}).map(([field, config]) => (
                    <div key={field} className="flex items-center justify-between p-3 border rounded-md3-lg hover:bg-md3-surface">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{field}</span>
                          <Badge className={getSensitivityColor(config.sensitivity)}>
                            {config.sensitivity}
                          </Badge>
                        </div>
                        {config.description && (
                          <p className="text-md3-body-small text-md3-on-surface-variant mt-1">{config.description}</p>
                        )}
                        {config.allowedRoles && config.allowedRoles.length > 0 && (
                          <div className="text-xs text-md3-on-surface-variant mt-1">
                            Allowed: {config.allowedRoles.map(r => 
                              `${r.department}${r.level !== '*' ? ` (${Array.isArray(r.level) ? r.level.join(', ') : r.level})` : ''}`
                            ).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                           onClick={() => openEditDialog(module, field)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Delete field "${field}"?`)) {
                              handleDeleteField(module, field);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {Object.keys(localConfig[module] || {}).length === 0 && (
                    <div className="text-center py-8 text-md3-on-surface-variant">
                      No fields in this module yet. Click "Add Field" to create one.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Add Module Dialog */}
      <Dialog open={addModuleDialog} onOpenChange={setAddModuleDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Add New Module</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Module Name *</Label>
              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="e.g., contracts, invoices, timesheets"
                value={newModule.name}
                onChange={(e) => setNewModule({ ...newModule, name: e.target.value.toLowerCase() })}
              />
              <p className="text-xs text-md3-on-surface-variant mt-1">
                Use lowercase, no spaces (use underscores if needed)
              </p>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Brief description of what this module contains"
                value={newModule.description}
                onChange={(e) => setNewModule({ ...newModule, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setAddModuleDialog(false)}>
              Cancel
            </Button>
            <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleAddModule}>
              Add Module
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Field Dialog */}
      <Dialog open={addFieldDialog} onOpenChange={setAddFieldDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Add New Field</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Module *</Label>
              <Select
                value={newField.module}
                onValueChange={(val) => setNewField({ ...newField, module: val, name: '' })}
              >
                <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                  <SelectValue placeholder="Select module" />
                </SelectTrigger>
                <SelectContent>
                  {modules.map(module => {
                    const availableCount = getAvailableFields(module, localConfig[module]).length;
                    const totalCount = MODULE_FIELDS[module]?.length || 0;
                    const configuredCount = totalCount - availableCount;
                    
                    return (
                      <SelectItem key={module} value={module}>
                        <div className="flex items-center justify-between w-full">
                          <span>{module}</span>
                          {totalCount > 0 && (
                            <span className="ml-2 text-xs text-md3-on-surface-variant">
                              ({configuredCount}/{totalCount} configured)
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {newField.module && MODULE_FIELDS[newField.module] && (
                <p className="text-xs text-md3-on-surface-variant mt-1">
                  {getAvailableFields(newField.module, localConfig[newField.module]).length} fields available to configure
                </p>
              )}
            </div>

            <div>
              <Label>Field Name *</Label>
              {newField.module && MODULE_FIELDS[newField.module] ? (
                <>
                  <Select
                    value={newField.name}
                    onValueChange={(val) => setNewField({ ...newField, name: val })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue placeholder="Select a field from the list" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableFields(newField.module, localConfig[newField.module]).length > 0 ? (
                        getAvailableFields(newField.module, localConfig[newField.module]).map(field => (
                          <SelectItem key={field} value={field}>
                            {field}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-1.5 text-md3-body-small text-md3-on-surface-variant">
                          All fields have been configured
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-md3-on-surface-variant mt-1">
                    Shows only fields not yet configured for this module
                  </p>
                </>
              ) : (
                <>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="e.g., salary, profit_margin, bank_account"
                    value={newField.name}
                    onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                  />
                  <p className="text-xs text-md3-on-surface-variant mt-1">
                    {newField.module 
                      ? "No predefined fields for this module - enter custom field name"
                      : "Select a module first to see available fields"}
                  </p>
                </>
              )}
            </div>

            <div>
              <Label>Sensitivity Level *</Label>
              <Select
                value={newField.sensitivity}
                onValueChange={(val) => setNewField({ ...newField, sensitivity: val })}
              >
                <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENSITIVITY.PUBLIC}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-800">PUBLIC</Badge>
                      <span className="text-md3-body-small">Everyone can see</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={SENSITIVITY.INTERNAL}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-800">INTERNAL</Badge>
                      <span className="text-md3-body-small">All logged-in users</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={SENSITIVITY.CONFIDENTIAL}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-yellow-100 text-yellow-800">CONFIDENTIAL</Badge>
                      <span className="text-md3-body-small">Specific roles only</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={SENSITIVITY.RESTRICTED}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-red-100 text-red-800">RESTRICTED</Badge>
                      <span className="text-md3-body-small">Admin only</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="What this field contains"
                value={newField.description}
                onChange={(e) => setNewField({ ...newField, description: e.target.value })}
              />
            </div>

            {(newField.sensitivity === SENSITIVITY.CONFIDENTIAL || 
              newField.sensitivity === SENSITIVITY.RESTRICTED) && (
              <div className="border-t pt-4">
                <Label>Allowed Roles (Required for CONFIDENTIAL/RESTRICTED)</Label>
                <div className="mt-2 space-y-3">
                  {/* Add Role Form */}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Department</Label>
                      <Select
                        value={currentRole.department}
                        onValueChange={(val) => setCurrentRole({ ...currentRole, department: val })}
                      >
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="*">All Departments</SelectItem>
                          {Object.values(DEPARTMENTS).map(dept => (
                            <SelectItem key={dept} value={dept}>
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Access Levels (optional)</Label>
                      <div className="flex gap-2 flex-wrap mt-1">
                        {Object.values(ACCESS_LEVELS).map(level => (
                          <div key={level} className="flex items-center space-x-1">
                            <Checkbox
                              id={`new-${level}`}
                              checked={currentRole.levels.includes(level)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setCurrentRole({
                                    ...currentRole,
                                    levels: [...currentRole.levels, level]
                                  });
                                } else {
                                  setCurrentRole({
                                    ...currentRole,
                                    levels: currentRole.levels.filter(l => l !== level)
                                  });
                                }
                              }}
                            />
                            <label htmlFor={`new-${level}`} className="text-xs">
                              {level}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button onClick={handleAddRole} size="sm">
                      Add Role
                    </Button>
                  </div>

                  {/* Display Added Roles */}
                  {newField.allowedRoles.length > 0 && (
                    <div className="space-y-2 mt-3">
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Added Roles:</Label>
                      {newField.allowedRoles.map((role, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-md3-surface rounded">
                          <span className="text-md3-body-small">
                            {role.department} {role.level !== '*' && `(${Array.isArray(role.level) ? role.level.join(', ') : role.level})`}
                          </span>
                          <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                             onClick={() => handleRemoveRole(index)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setAddFieldDialog(false)}>
              Cancel
            </Button>
            <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleAddField}>
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Field Dialog */}
      <Dialog open={editFieldDialog} onOpenChange={setEditFieldDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Edit Field: {selectedModule}.{selectedField}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Module (Read-only)</Label>
              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container" value={selectedModule || ''} disabled  />
            </div>

            <div>
              <Label>Field Name (Read-only)</Label>
              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container" value={selectedField || ''} disabled  />
            </div>

            <div>
              <Label>Sensitivity Level *</Label>
              <Select
                value={newField.sensitivity}
                onValueChange={(val) => setNewField({ ...newField, sensitivity: val })}
              >
                <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(SENSITIVITY).map(level => (
                    <SelectItem key={level} value={level}>
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="What this field contains"
                value={newField.description}
                onChange={(e) => setNewField({ ...newField, description: e.target.value })}
              />
            </div>

            {(newField.sensitivity === SENSITIVITY.CONFIDENTIAL || 
              newField.sensitivity === SENSITIVITY.RESTRICTED) && (
              <div className="border-t pt-4">
                <Label>Allowed Roles</Label>
                <div className="mt-2 space-y-3">
                  {/* Add Role Form */}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Department</Label>
                      <Select
                        value={currentRole.department}
                        onValueChange={(val) => setCurrentRole({ ...currentRole, department: val })}
                      >
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="*">All Departments</SelectItem>
                          {Object.values(DEPARTMENTS).map(dept => (
                            <SelectItem key={dept} value={dept}>
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Access Levels</Label>
                      <div className="flex gap-2 flex-wrap mt-1">
                        {Object.values(ACCESS_LEVELS).map(level => (
                          <div key={level} className="flex items-center space-x-1">
                            <Checkbox
                              id={`edit-role-${level}`}
                              checked={currentRole.levels.includes(level)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setCurrentRole({
                                    ...currentRole,
                                    levels: [...currentRole.levels, level]
                                  });
                                } else {
                                  setCurrentRole({
                                    ...currentRole,
                                    levels: currentRole.levels.filter(l => l !== level)
                                  });
                                }
                              }}
                            />
                            <label htmlFor={`edit-role-${level}`} className="text-xs">
                              {level}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button onClick={handleAddRole} size="sm">
                      Add Role
                    </Button>
                  </div>

                  {/* Display Added Roles */}
                  {newField.allowedRoles.length > 0 && (
                    <div className="space-y-2 mt-3">
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Current Roles:</Label>
                      {newField.allowedRoles.map((role, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-md3-surface rounded">
                          <span className="text-md3-body-small">
                            {role.department} {role.level !== '*' && `(${Array.isArray(role.level) ? role.level.join(', ') : role.level})`}
                          </span>
                          <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                             onClick={() => handleRemoveRole(index)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setEditFieldDialog(false)}>
              Cancel
            </Button>
            <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleEditField}>
              Update Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
